import { useNavigate } from 'react-router-dom';
import ProductCard from '../../components/feature/ProductCard';
import Footer from '../../components/feature/Footer';
import Header from '../../components/feature/Header';
import SEOHead from '../../components/feature/SEOHead';

export default function Home() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "ignight",
    "url": siteUrl,
    "description": "ig-night crafts concrete candles infused with pure essential oils. Each candle is a destination — light one and let the scent teleport you to Portofino, Provence, Scandinavia and beyond.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  const signatureProducts = [
    {
      id: 'portofino-morning',
      name: 'Portofino Morning',
      destination: 'Italian Riviera',
      price: 'CA$25',
      image: 'https://static.readdy.ai/image/e303b6d8f85cec63989e257b3b543d6f/495402795d5c018ad669106fdfe38d37.png',
      description: 'Sun-warmed citrus, clean linen, and soft neroli — a morning on the Italian coast.'
    },
    {
      id: 'fleur-de-nuit',
      name: 'Fleur de Nuit',
      destination: 'Paris at Midnight',
      price: 'CA$25',
      image: 'https://static.readdy.ai/image/e303b6d8f85cec63989e257b3b543d6f/96d7e3a6acb477d9e20976d786ab4dd6.png',
      description: 'Fresh roses, warm jasmine, and soft silk — a candlelit suite in Paris after dark.'
    },
    {
      id: 'velvet-study',
      name: 'Velvet Study',
      destination: 'English Countryside',
      price: 'CA$25',
      image: 'https://static.readdy.ai/image/e303b6d8f85cec63989e257b3b543d6f/a505396c8e4926ad7e295ea97cee7366.png',
      description: 'Polished wood, warm smoke, and leather — a private library after midnight.'
    },
    {
      id: 'lavender-terrace',
      name: 'Lavender Terrace',
      destination: 'Provence, France',
      price: 'CA$25',
      image: 'https://static.readdy.ai/image/e303b6d8f85cec63989e257b3b543d6f/824cb0538fbd7ad03af28ba2d3d75796.png',
      description: 'Lavender air, warm cream, and golden hour — a sunset terrace in Provence.'
    },
    {
      id: 'nordic-spa',
      name: 'Nordic Spa',
      destination: 'Scandinavia',
      price: 'CA$25',
      image: 'https://static.readdy.ai/image/e303b6d8f85cec63989e257b3b543d6f/f133e1f2fb337cf7f9c43493aab68580.png',
      description: 'Eucalyptus steam, cool mint, and clean stone — a luxury spa in the Nordic fjords.'
    }
  ];

  return (
    <>
      <SEOHead
        title="ignight"
        description="ig-night crafts concrete candles infused with pure essential oils. Each candle is a destination — light one and let the scent teleport you to Portofino, Provence, Scandinavia and beyond. Handmade luxury concrete vessel candles."
        keywords="concrete candles, essential oil candles, luxury handmade candles, scented concrete candles, destination candles"
        canonical={`${siteUrl}/`}
        schema={schema}
      />
      <div className="min-h-screen bg-[#0B1B3A]">
        <Header />
        {/* Hero Section */}
        <section className="bg-[#0B1B3A] pt-24 pb-8 sm:pt-28 sm:pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-serif text-[#F5F0E8] mb-4 sm:mb-6" style={{ fontFamily: 'Pacifico, serif' }}>
              ig-night
            </h1>
          </div>
        </section>

        {/* Signature Collection */}
        <section id="collections" className="pt-2 sm:pt-4 pb-8 sm:pb-12 bg-[#0B1B3A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-16">
              <p className="text-xs tracking-widest text-[#6B83A8] uppercase mb-2 sm:mb-3">Where will you go tonight?</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#F5F0E8] mb-3 sm:mb-4">Destinations Collection</h2>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6 lg:gap-8" data-product-shop>
              {signatureProducts.map((product, index) => {
                const isLast = index === signatureProducts.length - 1 && signatureProducts.length % 2 !== 0;
                return (
                  <div key={product.id} className={isLast ? 'col-span-2 lg:col-span-1 max-w-[50%] lg:max-w-none mx-auto lg:mx-0' : ''}>
                    <ProductCard
                      id={product.id}
                      name={product.name}
                      destination={product.destination}
                      description={product.description}
                      price={product.price}
                      image={product.image}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Our Craft Section */}
        <section id="craft" className="py-12 sm:py-20 bg-[#0E2244]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#F5F0E8] mb-4 sm:mb-6">Crafted to Transport</h2>
              <p className="text-sm sm:text-base text-[#8A9BB5] max-w-2xl mx-auto leading-relaxed">
                Every ig-night candle is a sensory passport. We blend pure essential oils into natural soy wax, hand-poured into raw concrete vessels — designed to take you somewhere the moment you light the wick.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-10">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#162D52] flex items-center justify-center mx-auto mb-4">
                  <i className="ri-flask-line text-[#D4C5A9] text-2xl"></i>
                </div>
                <h3 className="text-base sm:text-lg font-serif text-[#F5F0E8] mb-2">Pure Essential Oils</h3>
                <p className="text-xs sm:text-sm text-[#8A9BB5] leading-relaxed">
                  No synthetic fragrances. Every scent is built from 100% pure essential oils — carefully selected and blended to recreate the atmosphere of a real place.
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#162D52] flex items-center justify-center mx-auto mb-4">
                  <i className="ri-home-4-line text-[#D4C5A9] text-2xl"></i>
                </div>
                <h3 className="text-base sm:text-lg font-serif text-[#F5F0E8] mb-2">Concrete Vessels</h3>
                <p className="text-xs sm:text-sm text-[#8A9BB5] leading-relaxed">
                  Each candle is hand-poured into a raw concrete bowl — minimal, tactile, and built to last. Once the wax is gone, the vessel lives on as a beautiful object.
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#162D52] flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-line text-[#D4C5A9] text-2xl"></i>
                </div>
                <h3 className="text-base sm:text-lg font-serif text-[#F5F0E8] mb-2">Scent as Travel</h3>
                <p className="text-xs sm:text-sm text-[#8A9BB5] leading-relaxed">
                  Each candle is named after a place. The essential oil blend is designed to teleport you there — close your eyes, breathe in, and arrive.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section id="story" className="py-12 sm:py-20 bg-[#0B1B3A]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#F5F0E8] mb-6 sm:mb-8">Our Story</h2>
            <p className="text-sm sm:text-base lg:text-lg text-[#8A9BB5] leading-relaxed mb-6 sm:mb-8">
              ig-night was born from a simple idea: scent is the fastest way to travel. We started by asking — what if you could light a candle and be transported to the Italian Riviera, a Parisian balcony, or a Scandinavian spa?
            </p>
            <p className="text-sm sm:text-base lg:text-lg text-[#8A9BB5] leading-relaxed mb-6 sm:mb-8">
              We craft each candle by hand, blending pure essential oils into natural soy wax and pouring them into raw concrete vessels. No synthetic fragrances. No shortcuts. Just real oils, real craft, and real places captured in scent.
            </p>
            <p className="text-sm sm:text-base lg:text-lg text-[#8A9BB5] leading-relaxed">
              Every candle is a destination. Every flame is a departure. Light one, close your eyes, and let the essential oils take you there.
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
